import { useMutation } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { InviteClientHeader } from '@/components/invite-client/InviteClientHeader';
import { useToast } from '@/components/hooks/use-toast';

import { ChevronRight, Loader2 } from 'lucide-react';
import { ChevronLeft } from '@mui/icons-material';

import {
  useInviteClientContext,
  useInviteClientTrigger,
} from '@/components/invite-client/InviteClientContext';
import { selectSessionAdvisor, fetchClientsForAdvisorByAdvisorIdRequest } from '@client/model';
import { quickClientApi } from '@client/api/waterlily';

export function InviteClientQuickIntake() {
  const dispatch = useDispatch();
  const advisor = useSelector(selectSessionAdvisor);
  const { toast } = useToast();
  const { clearInviteClientParams } = useInviteClientTrigger();

  const {
    inviteClientConfig,
    handleStepChange,
    handleConfigChange,
  } = useInviteClientContext();

  const { firstName, lastName } = inviteClientConfig;

  const quickClientMutation = useMutation({
    mutationFn: quickClientApi.createQuickClientWithForm,
    onSuccess: data => {
      dispatch(
        fetchClientsForAdvisorByAdvisorIdRequest({
          advisorId: advisor.advisorId,
          isInvalidation: true,
        }),
      );
      dispatch({ type: 'RESUME_SESSION_REFRESH' });
      clearInviteClientParams();
      const baseUrl = window.location.origin;
      const formUrl = `${baseUrl}/onboarding-form/${data.formReferenceId}`;
      window.open(formUrl, '_blank', 'noopener,noreferrer');
    },
    onError: () => {
      toast({
        title: 'Failed to create client',
        description: 'Please try again or contact support',
        variant: 'destructive',
      });
    },
  });

  const canSubmit = firstName.trim().length > 0 && lastName.trim().length > 0;
  const isLoading = quickClientMutation.isPending;

  const handleSubmit = () => {
    if (!canSubmit) return;

    quickClientMutation.mutate({
      advisorId: advisor.advisorId,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      clientRelationshipStage: inviteClientConfig.clientRelationship,
    });
  };

  return (
    <>
      <InviteClientHeader
        title="Fill Intake Form for Client"
        description="Enter your client's name to add them and complete the intake form on their behalf. No invitation will be sent."
      />

      <div className="mt-4">
        <div className="flex flex-col gap-4 rounded-xl border border-gray-200 p-4">
          <div className="flex flex-col gap-1.5">
            <Label
              htmlFor="firstName"
              className="text-xs font-medium text-gray-700"
            >
              First name <span className="text-red-700">*</span>
            </Label>
            <Input
              id="firstName"
              type="text"
              placeholder="John"
              value={firstName}
              onChange={({ target }) => handleConfigChange('firstName', target.value)}
              disabled={isLoading}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label
              htmlFor="lastName"
              className="text-xs font-medium text-gray-700"
            >
              Last name <span className="text-red-700">*</span>
            </Label>
            <Input
              id="lastName"
              type="text"
              placeholder="Doe"
              value={lastName}
              onChange={({ target }) => handleConfigChange('lastName', target.value)}
              disabled={isLoading}
            />
          </div>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <Button
          variant="outline"
          onClick={() => handleStepChange('method')}
          disabled={isLoading}
        >
          <ChevronLeft className="size-4" />
          Back
        </Button>

        <Button
          disabled={!canSubmit || isLoading}
          onClick={handleSubmit}
        >
          {isLoading ? (
            <>
              <Loader2 className="size-4 animate-spin" />
              Creating...
            </>
          ) : (
            <>
              Add Client & Continue
              <ChevronRight className="size-4" />
            </>
          )}
        </Button>
      </div>
    </>
  );
}
